// ============================================================================
// BESPOKIAN Pharmacist Cockpit - Profile Dropdown
// User profile menu with settings and account options
// ============================================================================

import { Link } from '@tanstack/react-router';
import {
  User,
  Settings,
  LogOut,
  Shield,
  Bell,
  HelpCircle,
  FileText,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { clsx } from 'clsx';

interface ProfileDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenHelp: () => void;
}

export function ProfileDropdown({ isOpen, onClose, onOpenHelp }: ProfileDropdownProps) {
  const { user, logout, isPharmacist } = useAuth();

  if (!isOpen) return null;

  const handleLogout = () => {
    onClose();
    logout();
  };

  const handleHelpClick = () => {
    onClose();
    onOpenHelp();
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      {/* Dropdown */}
      <div className="absolute right-0 top-full mt-2 w-72 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
        {/* User Info Header */}
        <div className="px-4 py-4 bg-gradient-to-br from-pharmacy-primary to-pharmacy-primary/80">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 text-white flex items-center justify-center text-lg font-semibold ring-2 ring-white/30">
              {user?.firstName?.[0]}
              {user?.lastName?.[0]}
            </div>
            <div>
              <p className="text-white font-semibold">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-white/80 text-sm">{user?.email}</p>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <span
              className={clsx(
                'px-2 py-0.5 text-xs font-medium rounded-full',
                isPharmacist
                  ? 'bg-white/20 text-white'
                  : 'bg-amber-400/20 text-amber-100'
              )}
            >
              {isPharmacist ? 'Pharmacist' : 'Technician'}
            </span>
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-400/20 text-green-100">
              Active
            </span>
          </div>
        </div>

        {/* Menu Items */}
        <div className="py-2">
          {/* Profile Section */}
          <div className="px-2">
            <Link
              to="/settings"
              onClick={onClose}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition-colors group"
            >
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                <User className="h-4 w-4 text-slate-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">My Profile</p>
                <p className="text-xs text-slate-500">View and edit your profile</p>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-400" />
            </Link>

            <Link
              to="/settings"
              onClick={onClose}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition-colors group"
            >
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                <Settings className="h-4 w-4 text-slate-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">Settings</p>
                <p className="text-xs text-slate-500">App preferences</p>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-400" />
            </Link>
          </div>

          <div className="my-2 border-t border-slate-100" />

          {/* Quick Actions */}
          <div className="px-2">
            <Link
              to="/settings"
              onClick={onClose}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition-colors group"
            >
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                <Bell className="h-4 w-4 text-slate-600" />
              </div>
              <span className="text-sm text-slate-700">Notification Settings</span>
            </Link>

            <Link
              to="/settings"
              onClick={onClose}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition-colors group"
            >
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                <Shield className="h-4 w-4 text-slate-600" />
              </div>
              <span className="text-sm text-slate-700">Security & Privacy</span>
            </Link>

            <button
              onClick={handleHelpClick}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-slate-100 transition-colors group"
            >
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                <HelpCircle className="h-4 w-4 text-slate-600" />
              </div>
              <span className="text-sm text-slate-700">Help & Shortcuts</span>
            </button>
          </div>

          <div className="my-2 border-t border-slate-100" />

          {/* Footer Actions */}
          <div className="px-2">
            <a
              href="https://docs.bespokian.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition-colors group"
            >
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                <FileText className="h-4 w-4 text-slate-600" />
              </div>
              <span className="text-sm text-slate-700">Documentation</span>
            </a>

            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-red-50 transition-colors group"
            >
              <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                <LogOut className="h-4 w-4 text-red-600" />
              </div>
              <span className="text-sm text-red-600 font-medium">Sign Out</span>
            </button>
          </div>
        </div>

        {/* Version Info */}
        <div className="px-4 py-2 bg-slate-50 border-t border-slate-100">
          <p className="text-xs text-slate-400 text-center">
            BESPOKIAN Pharmacy v8.0 • Build 2026.01.13
          </p>
        </div>
      </div>
    </>
  );
}

